/**
 * The application store: one immutable state object, two subscriptions.
 *
 * Two stores, not one, because quotes and everything else change at different
 * rates. A quote frame arrives per symbol per tick; folding that into the same
 * object as the session and the book would re-render the ticket and the chart on
 * every tick of every symbol. The quote board batches its own notifications at
 * ~20 Hz, and only the market watch (and the chart's last price) subscribe.
 *
 * The React binding is `useSyncExternalStore`, which is the supported way to
 * read an external mutable store without tearing. `getSnapshot` returns the same
 * object reference until something actually changed — getting that wrong makes
 * React loop forever, so both stores replace the reference on write and never
 * mutate in place.
 */

import { useSyncExternalStore } from 'react';

import type { PreviewSymbol, AccountSnapshot } from '@weke/protocol';

import type { Quote } from '../data/types.js';
import type { RunningDeal } from '../lib/csv.js';
import type { ConnectionStatus } from '../protocol/client.js';
import type { Session } from '../protocol/session.js';
import type { BookState } from '../state/projection.js';
import { initialBook } from '../state/projection.js';

export type BannerTone = 'error' | 'warning' | 'info' | 'success';

export interface Banner {
  readonly id: string;
  readonly tone: BannerTone;
  readonly title: string;
  readonly detail: string;
  /** The machine code, kept for the audit line and for support. */
  readonly code: string | null;
  readonly at: string;
}

export interface AlertRow {
  readonly id: string;
  readonly kind: string;
  readonly detail: string;
  readonly at: string;
}

export interface AppState {
  readonly session: Session | null;
  readonly account: AccountSnapshot | null;
  /**
   * The server instant the current account snapshot was evaluated at.
   *
   * `account.positions[].mark_e8` and `floating_minor` are valued at this
   * instant, which is up to a poll interval behind the live quote stream. The
   * status bar prints it as `marks HH:MM:SS` so the positions table's `Now` and
   * `P&L` never look fresher than they are.
   */
  readonly snapshotAt: string | null;
  readonly symbols: readonly PreviewSymbol[];
  readonly book: BookState;
  readonly status: ConnectionStatus;
  readonly statusDetail: string | null;
  readonly serverOffsetMs: number;
  readonly latencyMs: number | null;
  readonly lastMessageAt: string | null;
  readonly selectedSymbol: string | null;
  readonly banners: readonly Banner[];
  readonly alerts: readonly AlertRow[];
  readonly deals: readonly RunningDeal[];
  readonly dealsLoadedAt: string | null;
  readonly dealsLoading: boolean;
  /** Session reference price per symbol, for the change column. */
  readonly dayOpen: Readonly<Record<string, string>>;
  readonly heartbeatMs: number;
  readonly accepting: boolean;
}

export function initialState(): AppState {
  return {
    session: null,
    account: null,
    snapshotAt: null,
    symbols: [],
    book: initialBook(),
    status: 'offline',
    statusDetail: null,
    serverOffsetMs: 0,
    latencyMs: null,
    lastMessageAt: null,
    selectedSymbol: null,
    banners: [],
    alerts: [],
    deals: [],
    dealsLoadedAt: null,
    dealsLoading: false,
    dayOpen: {},
    heartbeatMs: 15_000,
    accepting: true,
  };
}

type Listener = () => void;

class Store<T> {
  private state: T;
  private readonly listeners = new Set<Listener>();

  constructor(initial: T) {
    this.state = initial;
  }

  getSnapshot = (): T => this.state;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  protected setState(next: T): void {
    if (Object.is(next, this.state)) return;
    this.state = next;
    for (const listener of this.listeners) listener();
  }
}

class AppStore extends Store<AppState> {
  constructor() {
    super(initialState());
  }

  patch(partial: Partial<AppState>): void {
    this.setState({ ...this.getSnapshot(), ...partial });
  }

  setBook(book: BookState): void {
    this.patch({ book });
  }

  pushBanner(banner: Omit<Banner, 'id' | 'at'> & { at?: string; id?: string }): string {
    const id = banner.id ?? `b-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    const state = this.getSnapshot();
    const next: Banner = {
      id,
      tone: banner.tone,
      title: banner.title,
      detail: banner.detail,
      code: banner.code,
      at: banner.at ?? new Date().toISOString(),
    };
    // Keep the newest four; a wall of stacked errors hides the one that matters.
    this.patch({ banners: [next, ...state.banners].slice(0, 4) });
    return id;
  }

  dismissBanner(id: string): void {
    this.patch({ banners: this.getSnapshot().banners.filter((banner) => banner.id !== id) });
  }

  pushAlert(alert: AlertRow): void {
    const state = this.getSnapshot();
    this.patch({ alerts: [alert, ...state.alerts].slice(0, 50) });
  }

  clear(): void {
    this.setState(initialState());
  }
}

export const appStore = new AppStore();

/* ------------------------------------------------------------------ */
/* Quotes                                                              */
/* ------------------------------------------------------------------ */

interface QuoteSnapshot {
  readonly quotes: Readonly<Record<string, Quote>>;
  readonly updatedAt: number | null;
}

/**
 * Batched quote board.
 *
 * A per-frame notification at 3 ticks/s/symbol is already 9 re-renders a second
 * for three symbols, and at 2,500 streams it would be thousands. The board
 * coalesces into one notification per `FLUSH_MS`, keeping the **latest** quote
 * per symbol — which is all a price column can show (the server coalesces the
 * fan-out for the same reason, docs/10 §8).
 */
class QuoteBoard extends Store<QuoteSnapshot> {
  private pending: Record<string, Quote> = {};
  private timer: ReturnType<typeof setTimeout> | null = null;
  private lastUpdatedAt: number | null = null;

  constructor() {
    super({ quotes: {}, updatedAt: null });
  }

  ingest(quote: Quote): void {
    this.pending[quote.symbol] = quote;
    this.lastUpdatedAt = quote.receivedAtMs;
    if (this.timer !== null) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      const merged = { ...this.getSnapshot().quotes, ...this.pending };
      this.pending = {};
      this.setState({ quotes: merged, updatedAt: this.lastUpdatedAt });
    }, 60);
  }

  reset(): void {
    this.pending = {};
    this.lastUpdatedAt = null;
    this.setState({ quotes: {}, updatedAt: null });
  }
}

export const quoteBoard = new QuoteBoard();

/* ------------------------------------------------------------------ */
/* Hooks                                                               */
/* ------------------------------------------------------------------ */

export function useApp(): AppState {
  return useSyncExternalStore(appStore.subscribe, appStore.getSnapshot, appStore.getSnapshot);
}

export function useQuotes(): QuoteSnapshot {
  return useSyncExternalStore(quoteBoard.subscribe, quoteBoard.getSnapshot, quoteBoard.getSnapshot);
}
